import React, { useState, useEffect } from "react";
import {
  Box,
  Chip,
  Grid,
  TextField,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Checkbox,
  Autocomplete,
  Button,
  RadioGroup,
  Radio,
} from "@mui/material";
import { getAllVendorType } from "../../../api/ap_services";
import SectionCard from "./SectionCard";
import { makeStyles } from "@mui/styles";

const categoryOptions = [
  { value: "Private Limited", label: "Private Limited" },
  { value: "Partnership", label: "Partnership" },
  { value: "Limited Liability", label: "Limited Liability" },
  { value: "Public Listed", label: "Public Listed" },
  { value: "Proprietorship", label: "Proprietorship" },
  { value: "Other", label: "Other" },
];

const useStyles = makeStyles((theme) => ({
  customAutocompleteInput: {
    "& .MuiInputBase-sizeSmall": {
      height: "40px" /* Adjust the height as needed */,
    },
  },
}));
const CompanyDetails = ({
  formData,
  onFieldChange,
  errors,
  touched,
  setFieldValue,
  onOwnerContactBlur,
  paymentTermsOptions = [],
  inspectedByOptions = [],
  groupId,
}) => {
  const fieldError = (name) => Boolean(touched?.[name] && errors?.[name]);
  const fieldHelper = (name) => (touched?.[name] && errors?.[name]) || "";
  const fieldsEnabled = Boolean(groupId?.isAdmin);

  const shareholdingStatusValue = formData.shareholdingStatus || "";
  const handleShareholdingStatusChange = (value) => {
    setFieldValue?.("shareholdingStatus", value);
  };
  const isShareholdingStatusOtherSelected =
    shareholdingStatusValue === "Other";

  const classes = useStyles();

  const [vendorComboOptions, setVendorComboOptions] = useState([]);
  useEffect(() => {
    getAllVendorType(0, 9999).then((res) => {
      const list = res?.response?.responseObject ?? [];
      setVendorComboOptions(Array.isArray(list) ? list : []);
    });
  }, []);

  // In edit mode: when options load and we have vendorTypeId from API, set vendorCodeOption so the dropdown shows the vendor code
  const vendorTypeId = formData.vendorTypeId ?? formData.vendor_type_id;
  useEffect(() => {
    if (!setFieldValue || vendorTypeId == null || vendorTypeId === "") return;
    if (vendorComboOptions.length === 0) return;
    const match =
      vendorComboOptions.find(
        (opt) =>
          (opt.id ?? opt.vendorTypeId ?? opt.vendor_type_id) == vendorTypeId,
      ) ?? null;
    if (match) {
      setFieldValue("vendorCodeOption", match);
      setFieldValue("vendorTypeCode", match.code ?? "");
    }
  }, [vendorComboOptions, vendorTypeId, setFieldValue]);

  const [showSecondaryContact, setShowSecondaryContact] = useState(
    Boolean(
      formData.secondaryContactName ||
      formData.secondaryContactDesignation ||
      formData.secondaryContactNo ||
      formData.secondaryContactEmail,
    ),
  );
  const [secondaryContactSlots, setSecondaryContactSlots] = useState(() => {
    const has1 = Boolean(
      formData.secondaryContactName ||
      formData.secondaryContactDesignation ||
      formData.secondaryContactNo ||
      formData.secondaryContactEmail,
    );
    const has2 = Boolean(
      formData.secondaryContactName2 ||
      formData.secondaryContactDesignation2 ||
      formData.secondaryContactNo2 ||
      formData.secondaryContactEmail2,
    );
    const has3 = Boolean(
      formData.secondaryContactName3 ||
      formData.secondaryContactDesignation3 ||
      formData.secondaryContactNo3 ||
      formData.secondaryContactEmail3,
    );
    const any = has1 || has2 || has3;
    return any ? [has1, has2, has3] : [true, false, false];
  });

  const handleAddPrimary = () => {
    setShowSecondaryContact(true);
  };

  const handleAddMoreSecondary = () => {
    setSecondaryContactSlots((prev) => {
      const idx = prev.findIndex((v) => !v);
      if (idx === -1) return prev;
      const next = [...prev];
      next[idx] = true;
      return next;
    });
  };

  const handleRemoveSecondary = (slotIndex) => {
    const bases = [
      "secondaryContactName",
      "secondaryContactDesignation",
      "secondaryContactNo",
      "secondaryContactEmail",
    ];
    bases.forEach((base) => {
      const name = slotIndex === 0 ? base : `${base}${slotIndex + 1}`;
      setFieldValue?.(name, "");
    });
    setSecondaryContactSlots((prev) => {
      const next = [...prev];
      next[slotIndex] = false;
      return next;
    });
  };

  const getSecondaryFieldName = (base, index) =>
    index === 0 ? base : `${base}${index + 1}`;

  const secondaryContactVisibleCount =
    secondaryContactSlots.filter(Boolean).length;
  const canAddMoreSecondary = secondaryContactVisibleCount < 2;
  const [isPrimarySameAsOwner, setIsPrimarySameAsOwner] = useState(false);

  useEffect(() => {
    if (!isPrimarySameAsOwner) return;
    setFieldValue?.("primaryContactName", formData.ownerName || "");
    setFieldValue?.("primaryContactNo", formData.ownerContactNo || "");
    setFieldValue?.("primaryContactEmail", formData.ownerEmail || "");
  }, [
    isPrimarySameAsOwner,
    formData.ownerName,
    formData.ownerContactNo,
    formData.ownerEmail,
    setFieldValue,
  ]);

  const handlePrimarySameAsOwnerChange = (event) => {
    const checked = event.target.checked;
    setIsPrimarySameAsOwner(checked);
    if (!checked) return;
    setFieldValue?.("primaryContactName", formData.ownerName || "");
    setFieldValue?.("primaryContactNo", formData.ownerContactNo || "");
    setFieldValue?.("primaryContactEmail", formData.ownerEmail || "");
  };

  return (
      <Box>
        <SectionCard title="Company Details">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Company Name"
                name="companyName"
                value={formData.companyName || ""}
                onChange={onFieldChange}
                size="small"
                error={fieldError("companyName")}
                helperText={fieldHelper("companyName")}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl
                component="fieldset"
                error={fieldError("shareholdingStatus")}
                required
              >
                <FormLabel component="legend">Shareholding Status</FormLabel>
                <RadioGroup
                  row
                  value={shareholdingStatusValue}
                  onChange={(e) =>
                    handleShareholdingStatusChange(e.target.value)
                  }
                >
                  {categoryOptions.map((opt) => (
                    <FormControlLabel
                      key={opt.value}
                      value={opt.value}
                      control={<Radio />}
                      label={opt.label}
                    />
                  ))}
                </RadioGroup>
                {fieldHelper("shareholdingStatus") && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {fieldHelper("shareholdingStatus")}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            {isShareholdingStatusOtherSelected && (
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Other - Description"
                  name="shareholdingStatusOtherRemarks"
                  value={formData.shareholdingStatusOtherRemarks || ""}
                  onChange={onFieldChange}
                  size="small"
                  placeholder="Please describe"
                  error={fieldError("shareholdingStatusOtherRemarks")}
                  helperText={fieldHelper("shareholdingStatusOtherRemarks")}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={6}>
              <Autocomplete
                className={classes.customAutocompleteInput}
                options={vendorComboOptions}
                getOptionLabel={(option) => {
                  if (typeof option === "string") return option;
                   const code = option.code ?? "";
                const name = option.name ?? "";
                  // return option?.code ?? "";
                  return name || code;
                }}

                isOptionEqualToValue={(option, value) =>
                  (option?.id ??
                    option?.vendorTypeId ??
                    option?.vendor_type_id) ==
                  (value?.id ?? value?.vendorTypeId ?? value?.vendor_type_id)
                }
                value={formData.vendorCodeOption ?? null}
                onChange={(event, value) => {
                  if (!setFieldValue) return;
                  const id = value
                    ? (value.id ?? value.vendorTypeId ?? value.vendor_type_id)
                    : null;
                  const code = value ? (value.code ?? "") : "";
                  setFieldValue("vendorCodeOption", value);
                  setFieldValue("vendorTypeId", id);
                  setFieldValue("vendorTypeCode", code);
                  setFieldValue("vendorTypeSubClassification", "");
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    required
                    label="Vendor Group"
                    variant="outlined"
                    error={fieldError("vendorTypeId")}
                    helperText={fieldHelper("vendorTypeId")}
                  />
                )}
                size="small"
              />
            </Grid>
            {(formData.vendorTypeCode === "ZJCP" || formData.vendorTypeCode === "ZJYF") && (
              <Grid item xs={12} sm={6}>
                <FormControl
                  fullWidth
                  size="small"
                  error={fieldError("vendorTypeSubClassification")}
                  required
                >
                  <InputLabel id="vendor-classification-label">Vendor Classification</InputLabel>
                  <Select
                    labelId="vendor-classification-label"
                    label="Vendor Classification"
                    name="vendorTypeSubClassification"
                    value={formData.vendorTypeSubClassification || ""}
                    onChange={(e) => {
                      setFieldValue?.("vendorTypeSubClassification", e.target.value);
                    }}
                    MenuProps={{
                      PaperProps: {
                        style: {
                          maxHeight: 400,
                          zIndex: 9999,
                        },
                      },
                      style: {
                        zIndex: 9999,
                      },
                    }}
                  >
                    <MenuItem value="">
                      <em>Select Classification</em>
                    </MenuItem>
                    {formData.vendorTypeCode === "ZJCP" && [
                        <MenuItem key="Cutting" value="Cutting">Cutting</MenuItem>,
                        <MenuItem key="Stitching" value="Stitching">Stitching</MenuItem>,
                        <MenuItem key="Packing" value="Packing">Packing</MenuItem>,
                        <MenuItem key="S2P" value="Stitch to Pack (S2P)">Stitch to Pack (S2P)</MenuItem>,
                        <MenuItem key="C2P" value="Cut to Pack (C2P)">Cut to Pack (C2P)</MenuItem>,
                        <MenuItem key="PrintingEmbroidery" value="Printing Embroidery">Printing Embroidery</MenuItem>,
                        <MenuItem key="S2POther" value="S2P Other">S2P Other</MenuItem>
                    ]}
                    {formData.vendorTypeCode === "ZJYF" && [
                        <MenuItem key="Knitting" value="Knitting">Knitting</MenuItem>,
                        <MenuItem key="Bleaching" value="Bleaching">Bleaching</MenuItem>,
                        <MenuItem key="Dyeing" value="Dyeing">Dyeing</MenuItem>,
                        <MenuItem key="Compacting" value="Compacting">Compacting</MenuItem>,
                        <MenuItem key="Steaming" value="Steaming">Steaming</MenuItem>,
                        <MenuItem key="FabricPrinting" value="Fabric Printing">Fabric Printing</MenuItem>,
                        <MenuItem key="Calendaring" value="Calendaring">Calendaring</MenuItem>,
                        <MenuItem key="Curing" value="Curing">Curing</MenuItem>,
                        <MenuItem key="Stenter" value="Stenter">Stenter</MenuItem>,
                        <MenuItem key="Winding" value="Winding">Winding</MenuItem>,
                        <MenuItem key="Scouring" value="Scouring">Scouring</MenuItem>,
                        <MenuItem key="Washing" value="Washing">Washing</MenuItem>,
                        <MenuItem key="CPDOther" value="CPD Other">CPD Other</MenuItem>
                    ]}
                  </Select>
                  {fieldHelper("vendorTypeSubClassification") && (
                    <Typography
                      variant="caption"
                      color="error"
                      sx={{ mt: 0.5, display: "block" }}
                    >
                      {fieldHelper("vendorTypeSubClassification")}
                    </Typography>
                  )}
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                fullWidth
                required
                label='Nature of Business with "MODENIK LIFESTYLE PRIVATE LIMITED"'
                name="natureOfBusiness"
                value={formData.natureOfBusiness || ""}
                onChange={onFieldChange}
                size="small"
                error={fieldError("natureOfBusiness")}
                helperText={fieldHelper("natureOfBusiness")}
                multiline
                rows={1}
              />
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard title="Owner Details">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Name"
                name="ownerName"
                value={formData.ownerName || ""}
                onChange={onFieldChange}
                size="small"
                error={fieldError("ownerName")}
                helperText={fieldHelper("ownerName")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Father Name"
                name="ownerFathersName"
                value={formData.ownerFathersName || ""}
                onChange={onFieldChange}
                size="small"
                error={fieldError("ownerFathersName")}
                helperText={fieldHelper("ownerFathersName")}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Contact No"
                name="ownerContactNo"
                value={formData.ownerContactNo || ""}
                onChange={(e) => {
                  const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                  setFieldValue?.("isOwnerContactNoAlreadyExists", "N");
                  onFieldChange({
                    target: { name: "ownerContactNo", value },
                  });
                }}
                onBlur={onOwnerContactBlur}
                size="small"
                error={fieldError("ownerContactNo")}
                helperText={fieldHelper("ownerContactNo")}
                inputProps={{
                  inputMode: "numeric",
                  pattern: "[0-9]*",
                  maxLength: 10,
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                required
                label="Email ID"
                name="ownerEmail"
                type="email"
                value={formData.ownerEmail || ""}
                onChange={onFieldChange}
                size="small"
                error={fieldError("ownerEmail")}
                helperText={fieldHelper("ownerEmail")}
              />
            </Grid>
          </Grid>
        </SectionCard>

        <SectionCard title="Partner Details (If applicable)">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Name"
                name="partnerName"
                value={formData.partnerName || ""}
                onChange={onFieldChange}
                size="small"
                error={fieldError("partnerName")}
                helperText={fieldHelper("partnerName")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Contact No"
                name="partnerContactNo"
                value={formData.partnerContactNo || ""}
                onChange={onFieldChange}
                size="small"
                error={fieldError("partnerContactNo")}
                helperText={fieldHelper("partnerContactNo")}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Email ID"
                name="partnerEmail"
                type="email"
                value={formData.partnerEmail || ""}
                onChange={onFieldChange}
                size="small"
                error={fieldError("partnerEmail")}
                helperText={fieldHelper("partnerEmail")}
              />
            </Grid>
          </Grid>
        </SectionCard>

       <SectionCard title="Primary Contact Information">
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={isPrimarySameAsOwner}
                  onChange={handlePrimarySameAsOwnerChange}
                  color="primary"
                />
              }
              label="Same as Owner Details"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Name"
              name="primaryContactName"
              required
              value={formData.primaryContactName || ""}
              onChange={onFieldChange}
              size="small"
              InputProps={{ readOnly: isPrimarySameAsOwner }}
              error={fieldError("primaryContactName")}
              helperText={fieldHelper("primaryContactName")}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Designation"
              name="primaryContactDesignation"
              required
              value={formData.primaryContactDesignation || ""}
              onChange={onFieldChange}
              size="small"
              error={fieldError("primaryContactDesignation")}
              helperText={fieldHelper("primaryContactDesignation")}
            />
          </Grid>

          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              required
              label="Contact Number"
              name="primaryContactNo"
              value={formData.primaryContactNo || ""}
              onChange={onFieldChange}
              size="small"
              InputProps={{ readOnly: isPrimarySameAsOwner }}
              error={fieldError("primaryContactNo")}
              helperText={fieldHelper("primaryContactNo")}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <TextField
              fullWidth
              required
              label="E-mail ID"
              name="primaryContactEmail"
              type="email"
              value={formData.primaryContactEmail || ""}
              onChange={onFieldChange}
              size="small"
              InputProps={{ readOnly: isPrimarySameAsOwner }}
              error={fieldError("primaryContactEmail")}
              helperText={fieldHelper("primaryContactEmail")}
            />
          </Grid>
          <Grid item xs={12} sm={2} mt={0.5}>
            <Button
              color="primary"
              variant="contained"
              size="small"
              onClick={handleAddPrimary}
            >
              Add
            </Button>
          </Grid>
        </Grid>
      </SectionCard>

      {showSecondaryContact && (
        <SectionCard title="Secondary Contact Information">
          <Grid container spacing={2}>
            {secondaryContactSlots.map(
              (visible, index) =>
                visible && (
                  <React.Fragment key={index}>
                    {secondaryContactVisibleCount > 1 && (
                      <Grid
                        item
                        xs={12}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mb: 0.5,
                        }}
                      >
                        <Typography variant="subtitle2" color="text.secondary">
                          Contact {index + 1}
                        </Typography>
                        {index >= 1 && (
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => handleRemoveSecondary(index)}
                          >
                            Remove
                          </Button>
                        )}
                      </Grid>
                    )}
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Name"
                        name={getSecondaryFieldName(
                          "secondaryContactName",
                          index,
                        )}
                        value={
                          formData[
                            getSecondaryFieldName("secondaryContactName", index)
                          ] || ""
                        }
                        onChange={onFieldChange}
                        size="small"
                        error={fieldError(
                          getSecondaryFieldName("secondaryContactName", index),
                        )}
                        helperText={fieldHelper(
                          getSecondaryFieldName("secondaryContactName", index),
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Designation"
                        name={getSecondaryFieldName(
                          "secondaryContactDesignation",
                          index,
                        )}
                        value={
                          formData[
                            getSecondaryFieldName(
                              "secondaryContactDesignation",
                              index,
                            )
                          ] || ""
                        }
                        onChange={onFieldChange}
                        size="small"
                        error={fieldError(
                          getSecondaryFieldName(
                            "secondaryContactDesignation",
                            index,
                          ),
                        )}
                        helperText={fieldHelper(
                          getSecondaryFieldName(
                            "secondaryContactDesignation",
                            index,
                          ),
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Contact Number"
                        name={getSecondaryFieldName(
                          "secondaryContactNo",
                          index,
                        )}
                        value={
                          formData[
                            getSecondaryFieldName("secondaryContactNo", index)
                          ] || ""
                        }
                        onChange={onFieldChange}
                        size="small"
                        error={fieldError(
                          getSecondaryFieldName("secondaryContactNo", index),
                        )}
                        helperText={fieldHelper(
                          getSecondaryFieldName("secondaryContactNo", index),
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="E-mail ID"
                        name={getSecondaryFieldName(
                          "secondaryContactEmail",
                          index,
                        )}
                        type="email"
                        value={
                          formData[
                            getSecondaryFieldName(
                              "secondaryContactEmail",
                              index,
                            )
                          ] || ""
                        }
                        onChange={onFieldChange}
                        size="small"
                        error={fieldError(
                          getSecondaryFieldName("secondaryContactEmail", index),
                        )}
                        helperText={fieldHelper(
                          getSecondaryFieldName("secondaryContactEmail", index),
                        )}
                      />
                    </Grid>
                  </React.Fragment>
                ),
            )}
            {canAddMoreSecondary && (
              <Grid item xs={12} sm={2} sx={{ mt: 0.5 }}>
                <Button
                  color="primary"
                  variant="contained"
                  size="small"
                  onClick={handleAddMoreSecondary}
                >
                  Add More
                </Button>
              </Grid>
            )}
          </Grid>
        </SectionCard>
      )}

        <SectionCard title="">
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <FormControl
                fullWidth
                size="small"
                error={fieldError("paymentTerms")}
              >
                <InputLabel id="payment-terms-label">Payment Terms</InputLabel>
                <Select
                  labelId="payment-terms-label"
                  label="Payment Terms"
                  name="paymentTerms"
                  value={formData.paymentTerms || ""}
                  onChange={onFieldChange}
                >
                  <MenuItem value="">
                    <em>Select Payment Terms</em>
                  </MenuItem>
                  {paymentTermsOptions.map((option) => {
                    const optionValue = option?.code ?? option?.name ?? "";
                    const optionKey =
                      option?.paymentTermsId ?? option?.id ?? optionValue;
                    const optionLabel = [option?.code, option?.name]
                      .filter(Boolean)
                      .join(" - ");
                    return (
                      <MenuItem key={optionKey} value={optionValue}>
                        {optionLabel || optionValue}
                      </MenuItem>
                    );
                  })}
                </Select>
                {fieldHelper("paymentTerms") && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {fieldHelper("paymentTerms")}
                  </Typography>
                )}
              </FormControl>
            </Grid>

{["ZJCP", "ZJYF"].includes(formData.vendorTypeCode) && (
  <>
            <Grid item xs={12}>
              <FormControl
                component="fieldset"
                error={fieldError("vendorAgreementStatus")}
                required
              >
                <FormLabel component="legend">Vendor Agreement Status</FormLabel>
                <RadioGroup
                  row
                  value={formData.vendorAgreementStatus || ""}
                  onChange={(e) => {
                    setFieldValue?.("vendorAgreementStatus", e.target.value);
                    if (e.target.value !== "Yes") {
                      setFieldValue?.("unitInspectedBy", "");
                      setFieldValue?.("vendorEvaluationAttachment", null);
                    }
                  }}
                >
                  <FormControlLabel
                    value="Y"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="N"
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
                {fieldHelper("vendorAgreementStatus") && (
                  <Typography
                    variant="caption"
                    color="error"
                    sx={{ mt: 0.5, display: "block" }}
                  >
                    {fieldHelper("vendorAgreementStatus")}
                  </Typography>
                )}
              </FormControl>
            </Grid>

            {formData.vendorAgreementStatus === "Y" && (
              <>
                <Grid item xs={12} sm={6}>
                  <FormControl
                    fullWidth
                    size="small"
                    error={fieldError("unitInspectedBy")}
                  >
                    <InputLabel id="unit-inspected-label">Unit Inspected By</InputLabel>
                    <Select
                      labelId="unit-inspected-label"
                      label="Unit Inspected By"
                      name="unitInspectedBy"
                      value={formData.unitInspectedBy || ""}
                      onChange={onFieldChange}
                    >
                      <MenuItem value="">
                        <em>Select Inspected By</em>
                      </MenuItem>
                      {inspectedByOptions.map((option) => {
                        const optionValue = option?.userId ?? option?.name ?? "";
                        const optionKey =
                          option?.userId ?? option?.id ?? optionValue;
                        const optionLabel = [option?.name, option?.employeeId]
                          .filter(Boolean)
                          .join(" - ");
                        return (
                          <MenuItem key={optionKey} value={optionValue}>
                            {optionLabel || optionValue}
                          </MenuItem>
                        );
                      })}
                    </Select>
                    {fieldHelper("unitInspectedBy") && (
                      <Typography
                        variant="caption"
                        color="error"
                        sx={{ mt: 0.5, display: "block" }}
                      >
                        {fieldHelper("unitInspectedBy")}
                      </Typography>
                    )}
                  </FormControl>
                </Grid>

 <Grid item xs={12} sm={6}>
            <Typography
              variant="body2"
              sx={{ mb: 0.5 }}
              color={
                fieldError("vendorEvaluationAttachment")
                  ? "error"
                  : "text.primary"
              }
            >
              Vendor Evaluation Attachment{" "}
              <Typography component="span" color="error">
                *
              </Typography>
            </Typography>
            <input
              type="file"
              accept=".pdf,.doc,.docx,image/*"
              onChange={(e) => {
                const f = e.target.files?.[0];
                setFieldValue("vendorEvaluationAttachment", f || null);
                e.target.value = "";
              }}
              style={{ fontSize: "12px" }}
            />
            {formData.vendorEvaluationAttachment && (
              <Chip
                size="small"
                label={
                  formData.vendorEvaluationAttachment?.name ||
                  formData.vendorEvaluationAttachment
                }
                onDelete={() =>
                  setFieldValue("vendorEvaluationAttachment", null)
                }
                sx={{ mt: 0.5 }}
                variant="outlined"
              />
            )}
            {fieldHelper("vendorEvaluationAttachment") && (
              <Typography
                variant="caption"
                color="error"
                sx={{ mt: 0.5, display: "block" }}
              >
                {fieldHelper("vendorEvaluationAttachment")}
              </Typography>
            )}
          </Grid>
              </>
            )}
          </>
)}
         
          </Grid>
        </SectionCard>
      </Box>
  );
};

export default CompanyDetails;
